import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Filter, Fingerprint, Wallet, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { etStamp } from "@/lib/dates";
import OriginChip from "./OriginChip";
import { SortTH, applySort } from "./tableSort";

const PAGE_SIZE = 15;

const ROLE_BADGE = {
  admin: { label: "Admin", cls: "bg-red-100 text-red-700" },
  affiliate_manager: { label: "Leader", cls: "bg-violet-100 text-violet-700" },
  participant: { label: "Member", cls: "bg-blue-100 text-blue-700" },
};
const roleBadge = (m) =>
  m.is_super_admin
    ? { label: "Sup Admin", cls: "bg-red-100 text-red-800" }
    : ROLE_BADGE[m.role] || { label: m.role || "—", cls: "bg-slate-100 text-slate-600" };

const MEMBER_SORT = {
  member: (m) => (m.name || m.email || "").toLowerCase(),
  role: (m) => roleBadge(m).label,
  wallet: (m) => (m.node_label || "").toLowerCase(),
  sponsor: (m) => (m.created_by_label || "~").toLowerCase(),
  came_from: (m) => (m.origin?.label || "~").toLowerCase(),
  since: (m) => m.created_at || "",
  last_login: (m) => m.last_login_at || "",
};

export default function MembersPanel({ members, copy }) {
  const [role, setRole] = useState("all");
  const [sponsor, setSponsor] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [sort, setSort] = useState({ key: "since", dir: "desc" });
  const [page, setPage] = useState(1);

  const roleOpts = useMemo(() => Array.from(new Set(members.map((m) => roleBadge(m).label))).sort(), [members]);
  const sponsorOpts = useMemo(() => Array.from(new Set(members.map((m) => m.created_by_label).filter(Boolean))).sort(), [members]);

  const filtered = useMemo(() => {
    return members.filter((m) => {
      if (role !== "all" && roleBadge(m).label !== role) return false;
      if (sponsor !== "all" && (m.created_by_label || "") !== sponsor) return false;
      if (from && (m.created_at || "") < from) return false;
      if (to && (m.created_at || "") > `${to}T23:59:59`) return false;
      return true;
    });
  }, [members, role, sponsor, from, to]);

  const sorted = useMemo(() => applySort(filtered, sort, MEMBER_SORT), [filtered, sort]);
  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const curPage = Math.min(page, totalPages);
  const rows = sorted.slice((curPage - 1) * PAGE_SIZE, curPage * PAGE_SIZE);

  useEffect(() => { setPage(1); }, [role, sponsor, from, to, sort]);
  const anyFilter = role !== "all" || sponsor !== "all" || from || to;

  return (
    <div data-testid="members-panel">
      {/* Filters */}
      <div className="glass p-3 mb-4 flex flex-wrap items-center gap-2" data-testid="members-filters">
        <span className="text-ink-3 inline-flex items-center gap-1.5 text-xs font-semibold pl-1"><Filter size={13} /> Filter</span>
        <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="member-filter-role"
          className="text-xs rounded-lg border border-black/10 bg-white px-2.5 py-1.5">
          <option value="all">Any role</option>
          {roleOpts.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <select value={sponsor} onChange={(e) => setSponsor(e.target.value)} data-testid="member-filter-sponsor"
          className="text-xs rounded-lg border border-black/10 bg-white px-2.5 py-1.5">
          <option value="all">Any sponsor</option>
          {sponsorOpts.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <label className="text-xs text-ink-3 inline-flex items-center gap-1.5">Member since
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="member-filter-from"
            className="rounded-lg border border-black/10 bg-white px-2 py-1" />
        </label>
        <label className="text-xs text-ink-3 inline-flex items-center gap-1.5">To
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="member-filter-to"
            className="rounded-lg border border-black/10 bg-white px-2 py-1" />
        </label>
        {anyFilter && (
          <button onClick={() => { setRole("all"); setSponsor("all"); setFrom(""); setTo(""); }}
            data-testid="member-filter-clear" className="text-xs text-blue-700 hover:underline ml-auto">Clear</button>
        )}
        <span className={`text-xs text-ink-3 ${anyFilter ? "" : "ml-auto"}`} data-testid="members-count">{filtered.length} {filtered.length === 1 ? "member" : "members"}</span>
      </div>

      {/* Table */}
      <div className="overflow-x-auto glass" style={{ borderRadius: 16 }}>
        <table className="modern prospects-table w-full text-sm">
          <thead>
            <tr>
              <SortTH label="Member" k="member" sort={sort} setSort={setSort} />
              <SortTH label="Role" k="role" sort={sort} setSort={setSort} />
              <SortTH label="CaryPact wallet" k="wallet" sort={sort} setSort={setSort} />
              <SortTH label="Sponsor" k="sponsor" sort={sort} setSort={setSort} />
              <SortTH label="Came from" k="came_from" sort={sort} setSort={setSort} />
              <SortTH label="Member since · ET" k="since" sort={sort} setSort={setSort} />
              <SortTH label="Last login · ET" k="last_login" sort={sort} setSort={setSort} />
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan="8" className="text-center py-10 text-ink-3" data-testid="members-empty">No members match these filters.</td></tr>
            ) : rows.map((m) => {
              const rb = roleBadge(m);
              return (
                <tr key={m.id} data-testid={`member-row-${m.id}`}>
                  <td>
                    <div className="font-medium text-xs">{m.name || "—"}</div>
                    {m.email && (
                      <button onClick={() => copy && copy(m.email)} className="text-[11px] text-ink-3 font-mono hover:text-ink-1" title="Copy email">{m.email}</button>
                    )}
                  </td>
                  <td><span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${rb.cls}`} data-testid={`member-role-${m.id}`}>{rb.label}</span></td>
                  <td>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800"
                      title="Their own CaryPact wallet + link synced into our tree" data-testid={`member-wallet-${m.id}`}>
                      <Wallet size={10} /> {m.node_label || "CaryPact wallet"}
                    </span>
                  </td>
                  <td className="text-[11px]">{m.created_by_label || <span className="text-ink-3">—</span>}</td>
                  <td className="text-[11px]" data-testid={`member-came-from-${m.id}`}>
                    <OriginChip origin={m.origin} testid={`origin-chip-${m.id}`} />
                  </td>
                  <td className="text-[11px] whitespace-nowrap">{m.created_at ? etStamp(m.created_at) : "—"}</td>
                  <td className="text-[11px] whitespace-nowrap">{m.last_login_at ? etStamp(m.last_login_at) : <span className="text-ink-3">never</span>}</td>
                  <td className="text-right">
                    {m.email && (
                      <Link to={`/admin/trace?email=${encodeURIComponent(m.email)}`} data-testid={`member-trace-${m.id}`}
                        className="text-[11px] text-ink-3 hover:text-ink-1 inline-flex items-center gap-1">
                        <Fingerprint size={11} /> Trace
                      </Link>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3 mt-4" data-testid="members-pagination">
          <Button size="sm" variant="outline" disabled={curPage <= 1} onClick={() => setPage(curPage - 1)} data-testid="members-prev"><ChevronLeft size={14} /> Prev</Button>
          <span className="text-xs text-ink-3">Page {curPage} of {totalPages}</span>
          <Button size="sm" variant="outline" disabled={curPage >= totalPages} onClick={() => setPage(curPage + 1)} data-testid="members-next">Next <ChevronRight size={14} /></Button>
        </div>
      )}
    </div>
  );
}
